import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import CompanySearch from '@/components/CompanySearch';
import RequesterSearch from '@/components/RequesterSearch';
import ProblemTypeSearch from '@/components/ProblemTypeSearch';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';
import { getTicketHoursIncludingSubtasks } from '@/lib/ticketHours';
import { Clock, CheckCircle2, XCircle, Loader2, AlertCircle, Edit, Trash2, MessageSquare, Paperclip, Plus, Building2, CheckSquare, Square, UserCircle, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || '';
const API = `${BACKEND_URL}/api`;

const getStatusBadge = (status) => {
  const variants = {
    open: { color: 'bg-blue-600 text-white', icon: Clock },
    in_progress: { color: 'bg-yellow-500 text-white', icon: Loader2 },
    resolved: { color: 'bg-green-600 text-white', icon: CheckCircle2 },
    closed: { color: 'bg-gray-600 text-white', icon: XCircle }
  };
  const variant = variants[status] || variants.open;
  const Icon = variant.icon;
  return (
    <Badge className={`${variant.color} flex items-center gap-1 font-bold uppercase`}>
      <Icon className="w-3 h-3" />
      {status?.replace('_', ' ')}
    </Badge>
  );
};

const getPriorityBadge = (priority) => {
  const variants = {
    critical: { color: 'bg-red-600 text-white border-red-700', icon: AlertCircle },
    high: { color: 'bg-orange-500 text-white border-orange-600', icon: AlertCircle },
    medium: { color: 'bg-yellow-500 text-white border-yellow-600', icon: Clock },
    low: { color: 'bg-green-500 text-white border-green-600', icon: CheckCircle2 }
  };
  const variant = variants[priority] || variants.medium;
  const Icon = variant.icon;
  return (
    <Badge className={`${variant.color} flex items-center gap-1 border font-bold uppercase`}>
      <Icon className="w-3 h-3" />
      {priority}
    </Badge>
  );
};

export default function TicketDialog({ ticketId, open, onOpenChange, user, companies = [], onUpdate }) {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);

  // States from Dashboard
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const [timeLogDialogOpen, setTimeLogDialogOpen] = useState(false);
  const [timeLog, setTimeLog] = useState({ hours: '', description: '', logged_at: '' });
  const [timeLogLoading, setTimeLogLoading] = useState(false);

  const [editingTimeLogId, setEditingTimeLogId] = useState(null);
  const [editingTimeLogForm, setEditingTimeLogForm] = useState({ hours: '', description: '', logged_at: '' });

  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);

  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', url: '', notes: '' });

  // Subtask state
  const [subtaskInput, setSubtaskInput] = useState('');
  const [subtaskDescription, setSubtaskDescription] = useState('');
  const [subtaskAssignee, setSubtaskAssignee] = useState('');
  const [subtaskDueDate, setSubtaskDueDate] = useState('');
  const [subtaskTimeEstimate, setSubtaskTimeEstimate] = useState('');
  const [subtaskProblemType, setSubtaskProblemType] = useState('');
  const [subtaskLoading, setSubtaskLoading] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskForm, setEditingSubtaskForm] = useState({ title: '', description: '', assignee_email: '', due_date: '', time_estimate: '', problem_type: '', status: '' });
  const [teamMembers, setTeamMembers] = useState([]);
  const [subtaskCommentText, setSubtaskCommentText] = useState({});
  const [subtaskCommentLoading, setSubtaskCommentLoading] = useState({});
  const [expandedSubtaskComments, setExpandedSubtaskComments] = useState({});

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchTicket = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, getAuthHeaders());
      setSelectedTicket(response.data);
      setEditedDescription(response.data.description);
      setEditedTitle(response.data.title);
    } catch (error) {
      toast.error('Failed to load ticket details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const openTrackingPage = async () => {
    const trackingWindow = window.open('', '_blank');
    try {
      const response = await axios.get(`${API}/tickets/${selectedTicket.id}/tracking-url`, getAuthHeaders());
      if (trackingWindow) trackingWindow.location.href = response.data.tracking_url;
      else window.open(response.data.tracking_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      if (trackingWindow) trackingWindow.close();
      toast.error(error.response?.data?.detail || 'Failed to open tracking page');
    }
  };

  useEffect(() => {
    if (open && ticketId) {
      fetchTicket();
    } else {
      setSelectedTicket(null);
      setIsEditingDescription(false);
      setIsEditingTitle(false);
      setShowDeleteConfirm(false);
    }
  }, [open, ticketId]);

  const notifyUpdate = () => {
    if (onUpdate) onUpdate();
  };

  // Load team members for subtask assignee
  useEffect(() => {
    if (open && (user?.role === 'admin' || user?.role === 'subadmin')) {
      axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(r => setTeamMembers((r.data || []).filter(u => u.role === 'admin' || u.role === 'subadmin')))
        .catch(() => {});
    }
  }, [open]);

  // --- Subtask Handlers ---
  const handleAddSubtask = async () => {
    if (!subtaskInput.trim()) return;
    setSubtaskLoading(true);
    try {
      const res = await axios.post(
        `${API}/tickets/${selectedTicket.id}/subtasks`,
        { title: subtaskInput.trim(), description: subtaskDescription.trim() || null, assignee_email: subtaskAssignee || null, due_date: subtaskDueDate || null, time_estimate: subtaskTimeEstimate ? parseFloat(subtaskTimeEstimate) : null, tags: subtaskProblemType ? [subtaskProblemType] : [] },
        getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setSubtaskInput(''); setSubtaskDescription(''); setSubtaskAssignee(''); setSubtaskDueDate(''); setSubtaskTimeEstimate(''); setSubtaskProblemType('');
      setShowSubtaskForm(false);
      notifyUpdate();
      toast.success('Subtask added');
    } catch { toast.error('Failed to add subtask'); }
    finally { setSubtaskLoading(false); }
  };

  const handleToggleSubtaskStatus = async (subtask) => {
    const nextStatus = subtask.status === 'done' ? 'open' : subtask.status === 'open' ? 'in_progress' : 'done';
    try {
      const res = await axios.patch(`${API}/tickets/${selectedTicket.id}/subtasks/${subtask.id}`, { status: nextStatus }, getAuthHeaders());
      setSelectedTicket(res.data); notifyUpdate();
    } catch { toast.error('Failed to update subtask'); }
  };

  const handleUpdateSubtask = async (subtaskId) => {
    try {
      const res = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}`,
        { title: editingSubtaskForm.title, description: editingSubtaskForm.description || null, assignee_email: editingSubtaskForm.assignee_email || null, due_date: editingSubtaskForm.due_date || null, time_estimate: editingSubtaskForm.time_estimate ? parseFloat(editingSubtaskForm.time_estimate) : null, tags: editingSubtaskForm.problem_type ? [editingSubtaskForm.problem_type] : [], status: editingSubtaskForm.status || 'open' },
        getAuthHeaders()
      );
      setSelectedTicket(res.data);
      setEditingSubtaskId(null);
      notifyUpdate();
      toast.success('Subtask updated');
    } catch { toast.error('Failed to update subtask'); }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    try {
      const res = await axios.delete(`${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}`, getAuthHeaders());
      setSelectedTicket(res.data); notifyUpdate(); toast.success('Subtask deleted');
    } catch { toast.error('Failed to delete subtask'); }
  };

  const handleAddSubtaskComment = async (subtaskId) => {
    const text = subtaskCommentText[subtaskId]?.trim();
    if (!text) return;
    setSubtaskCommentLoading(prev => ({ ...prev, [subtaskId]: true }));
    try {
      const res = await axios.post(`${API}/tickets/${selectedTicket.id}/subtasks/${subtaskId}/comments`, { text }, getAuthHeaders());
      setSelectedTicket(res.data);
      setSubtaskCommentText(prev => ({ ...prev, [subtaskId]: '' }));
      notifyUpdate(); toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubtaskCommentLoading(prev => ({ ...prev, [subtaskId]: false })); }
  };

  // --- Handlers ---
  const handleUpdateTitle = async () => {
    if (!editedTitle.trim()) return;
    try {
      const response = await axios.patch(`${API}/tickets/${selectedTicket.id}`, { title: editedTitle }, getAuthHeaders());
      toast.success('Title updated');
      setIsEditingTitle(false);
      setSelectedTicket(response.data);
      notifyUpdate();
    } catch (error) { toast.error('Failed to update title'); }
  };

  const handleUpdateDescription = async () => {
    try {
      const response = await axios.patch(`${API}/tickets/${selectedTicket.id}`, { description: editedDescription }, getAuthHeaders());
      toast.success('Description updated');
      setSelectedTicket(response.data);
      setIsEditingDescription(false);
      notifyUpdate();
    } catch (error) { toast.error('Failed to update description'); }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const response = await axios.patch(`${API}/tickets/${id}`, { status: newStatus }, getAuthHeaders());
      toast.success('Status updated');
      setSelectedTicket(response.data);
      notifyUpdate();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const handleUpdateTicketField = async (id, field, value) => {
    try {
      const payload = { [field]: value };
      if (field === 'due_date' && value) {
        payload.due_date = new Date(value + 'T00:00:00').toISOString();
      }
      const response = await axios.patch(`${API}/tickets/${id}`, payload, getAuthHeaders());
      toast.success(`${field.replace('_', ' ')} updated`);
      setSelectedTicket(response.data);
      notifyUpdate();
    } catch (error) { toast.error(`Failed to update ${field.replace('_', ' ')}`); }
  };

  const handleCreateCompanyInline = async (e) => {
    e.preventDefault();
    if (!newCompanyForm.name.trim()) { toast.error('Company name is required'); return; }
    try {
      const res = await axios.post(`${API}/companies`, newCompanyForm, getAuthHeaders());
      toast.success(`Company "${res.data.name}" created`);
      setAddCompanyOpen(false);
      setNewCompanyForm({ name: '', url: '', notes: '' });
      await handleUpdateTicketField(selectedTicket.id, 'company_id', res.data.id);
      notifyUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleDeleteTicket = async (id) => {
    try {
      await axios.delete(`${API}/tickets/${id}`, getAuthHeaders());
      toast.success('Ticket deleted successfully');
      onOpenChange(false);
      notifyUpdate();
    } catch (error) { toast.error('Failed to delete ticket'); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() && commentAttachments.length === 0) return;
    setCommentLoading(true);
    try {
      const response = await axios.post(`${API}/tickets/${selectedTicket.id}/comments`, { text: commentText, attachments: commentAttachments }, getAuthHeaders());
      setSelectedTicket(response.data);
      setCommentText('');
      setCommentAttachments([]);
      toast.success('Comment added');
      notifyUpdate();
    } catch (error) { toast.error('Failed to add comment'); } finally { setCommentLoading(false); }
  };

  const handleUpdateComment = async (commentId) => {
    try {
      const response = await axios.patch(`${API}/tickets/${selectedTicket.id}/comments/${commentId}`, { text: editingCommentText }, getAuthHeaders());
      toast.success('Comment updated');
      setSelectedTicket(response.data);
      setEditingCommentId(null);
      notifyUpdate();
    } catch (error) { toast.error('Failed to update comment'); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File size must be less than 5MB"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/upload`, formData, { headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } });
      setCommentAttachments([...commentAttachments, response.data.url]);
      toast.success("File attached");
    } catch (error) { toast.error("Failed to upload file"); }
  };

  const removeAttachment = (index) => {
    const newAttachments = [...commentAttachments];
    newAttachments.splice(index, 1);
    setCommentAttachments(newAttachments);
  };

  const handleAddTimeLog = async (e) => {
    e.preventDefault();
    if (!timeLog.hours || !timeLog.description.trim()) return;
    setTimeLogLoading(true);
    try {
      const payload = {
        hours: parseFloat(timeLog.hours),
        description: timeLog.description,
      };
      if (timeLog.logged_at) {
        payload.logged_at = new Date(timeLog.logged_at + 'T12:00:00').toISOString();
      }
      const response = await axios.post(`${API}/tickets/${selectedTicket.id}/time-log`, payload, getAuthHeaders());
      setSelectedTicket(response.data);
      setTimeLog({ hours: '', description: '', logged_at: '' });
      setTimeLogDialogOpen(false);
      toast.success('Time logged successfully');
      notifyUpdate();
    } catch (error) { toast.error('Failed to log time'); } finally { setTimeLogLoading(false); }
  };

  const handleUpdateTimeLog = async (logId) => {
    const targetForm = editingTimeLogForm;
    if (!targetForm.hours || !targetForm.description.trim()) return;
    try {
      const payload = {
        hours: parseFloat(targetForm.hours),
        description: targetForm.description,
      };
      if (targetForm.logged_at) {
        payload.logged_at = new Date(targetForm.logged_at + 'T12:00:00').toISOString();
      }
      const response = await axios.patch(`${API}/tickets/${selectedTicket.id}/time-log/${logId}`, payload, getAuthHeaders());
      toast.success('Time log updated');
      setSelectedTicket(response.data);
      setEditingTimeLogId(null);
      notifyUpdate();
    } catch (error) { toast.error('Failed to update time log'); }
  };

  if (!selectedTicket && loading) return null;

  const isAdminOrSubadmin = user?.role === 'admin' || user?.role === 'subadmin';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
              <DialogPrimitive.Content 
                data-testid="view-ticket-dialog"
                className="relative w-full max-w-[95vw] sm:max-w-3xl bg-white shadow-xl sm:rounded-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200"
              >
                {selectedTicket && (
                  <div className="flex flex-col relative bg-white">
                    <div className="p-4 sm:p-6 pb-3 border-b border-gray-100 sticky top-0 bg-white z-20 shadow-sm">
                <DialogHeader className="space-y-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">#{selectedTicket.id.slice(-6)}</span>
                        <span>•</span>
                        <span>{new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* Admin Actions Top Right */}
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={openTrackingPage} className="h-8 text-xs gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5" />
                          Tracking page
                        </Button>
                        <ClickUpTaskButton taskId={selectedTicket.clickup_task_id} />
                        {user?.role === 'admin' && (
                          <div className="flex items-center gap-2">
                            {showDeleteConfirm ? (
                              <div className="flex items-center gap-2 bg-red-50 p-1 rounded-md border border-red-200 animate-in fade-in slide-in-from-right-2">
                                <span className="text-xs font-medium text-red-600 px-1">Sure?</span>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteTicket(selectedTicket.id)}
                                  className="h-6 px-2 text-xs"
                                >
                                  Yes
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowDeleteConfirm(false)}
                                  className="h-6 px-2 text-xs hover:bg-red-100 hover:text-red-700"
                                >
                                  No
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-full text-gray-400 hover:text-gray-900"
                          onClick={() => onOpenChange(false)}
                        >
                          <span className="sr-only">Close</span>
                          <XCircle className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                    {isEditingTitle ? (
                      <div className="flex items-center gap-2 mt-1 pr-4">
                        <Input
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className="text-2xl font-bold h-10 w-full"
                          autoFocus
                        />
                        <Button size="sm" onClick={handleUpdateTitle} className="bg-black hover:bg-gray-800">Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setIsEditingTitle(false); setEditedTitle(selectedTicket.title) }}>Cancel</Button>
                      </div>
                    ) : (
                      <DialogTitle className="text-xl font-bold text-gray-900 leading-tight pr-4 flex items-center gap-2 group">
                        {selectedTicket.title}
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditingTitle(true)}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit className="w-4 h-4 text-gray-400 hover:text-gray-900" />
                          </Button>
                        )}
                      </DialogTitle>
                    )}
                  </div>
                </DialogHeader>
              </div>

              <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">

                  {/* Ticket Metadata - now inside scrollable area */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pb-3 border-b border-gray-100">
                    {/* Status */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
                      <div>
                        {isAdminOrSubadmin ? (
                          <Select
                            value={selectedTicket.status}
                            onValueChange={(value) => handleUpdateStatus(selectedTicket.id, value)}
                          >
                            <SelectTrigger className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0">
                              {getStatusBadge(selectedTicket.status)}
                            </SelectTrigger>
                            <SelectContent align="start">
                              <SelectItem value="open">{getStatusBadge('open')}</SelectItem>
                              <SelectItem value="in_progress">{getStatusBadge('in_progress')}</SelectItem>
                              <SelectItem value="resolved">{getStatusBadge('resolved')}</SelectItem>
                              <SelectItem value="closed">{getStatusBadge('closed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          getStatusBadge(selectedTicket.status)
                        )}
                      </div>
                    </div>

                    {/* Priority */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Priority</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <Select
                            value={selectedTicket.priority}
                            onValueChange={(value) => handleUpdateTicketField(selectedTicket.id, 'priority', value)}
                          >
                            <SelectTrigger className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0">
                              {getPriorityBadge(selectedTicket.priority)}
                            </SelectTrigger>
                            <SelectContent align="start">
                              <SelectItem value="low">{getPriorityBadge('low')}</SelectItem>
                              <SelectItem value="medium">{getPriorityBadge('medium')}</SelectItem>
                              <SelectItem value="high">{getPriorityBadge('high')}</SelectItem>
                              <SelectItem value="critical">{getPriorityBadge('critical')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          getPriorityBadge(selectedTicket.priority)
                        )}
                      </div>
                    </div>

                    {/* Due Date */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Due Date</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <Input
                            type="date"
                            value={selectedTicket.due_date ? selectedTicket.due_date.substring(0, 10) : ''}
                            onChange={(e) => handleUpdateTicketField(selectedTicket.id, 'due_date', e.target.value)}
                            className="h-8 w-full text-xs border-gray-200 hover:border-gray-300 focus:ring-0"
                          />
                        ) : (
                          <span className="text-xs text-gray-600 h-8 flex items-center">
                            {selectedTicket.due_date ? new Date(selectedTicket.due_date).toLocaleDateString() : '—'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Problem Type */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Problem</span>
                      <div>
                        {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) ? (
                          <ProblemTypeSearch
                            value={selectedTicket.problem_type}
                            onChange={(ptValue) => handleUpdateTicketField(selectedTicket.id, 'problem_type', ptValue)}
                            placeholder="Set problem type..."
                            allowCreate={true}
                            inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0"
                          />
                        ) : (
                          <Badge variant="outline" className="text-xs bg-gray-50">
                            {selectedTicket.problem_type}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Company (admin only) */}
                    {isAdminOrSubadmin && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Company</span>
                        <div>
                          <CompanySearch
                            value={selectedTicket.company_id || null}
                            onChange={(id) => handleUpdateTicketField(selectedTicket.id, 'company_id', id || null)}
                            companies={companies}
                            placeholder="Search company..."
                            inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0"
                          />
                        </div>
                      </div>
                    )}

                    {/* Requested By (admin only) */}
                    {isAdminOrSubadmin && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Requested By</span>
                        <div>
                          <RequesterSearch
                            value={selectedTicket.requested_by || null}
                            onChange={(id) => handleUpdateTicketField(selectedTicket.id, 'requested_by', id || null)}
                            placeholder="Search requester..."
                            inputClassName="h-8 text-xs border-gray-200 hover:border-gray-300 focus-visible:ring-0"
                          />
                        </div>
                      </div>
                    )}

                    {/* Hours Logged */}
                    {getTicketHoursIncludingSubtasks(selectedTicket) > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Time</span>
                        <div className="flex items-center gap-1.5 text-xs font-medium text-orange-700 bg-orange-50 px-2.5 py-1.5 rounded-md border border-orange-100 h-8">
                          <Clock className="w-3.5 h-3.5" />
                          {getTicketHoursIncludingSubtasks(selectedTicket)}h total
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick close action */}
                  {(isAdminOrSubadmin || selectedTicket.created_by === user?.id) && selectedTicket.status !== 'closed' && (
                    <div className="-mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-sm uppercase tracking-wider text-[10px] h-7"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        Mark as Closed
                      </Button>
                    </div>
                  )}
                  {/* Description Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">Description</Label>
                      {(selectedTicket.created_by === user?.id || isAdminOrSubadmin) && (
                        !isEditingDescription && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditingDescription(true)}
                            className="h-6 px-2 text-xs text-gray-500 hover:text-gray-900"
                          >
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        )
                      )}
                    </div>

                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          rows={6}
                          className="bg-white"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setIsEditingDescription(false)}>Cancel</Button>
                          <Button size="sm" onClick={handleUpdateDescription} className="bg-black hover:bg-gray-800">Save</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedTicket.description}</p>
                      </div>
                    )}
                  </div>

                  {isAdminOrSubadmin && (
                    <>
                      {/* Subtasks Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Subtasks
                            {selectedTicket.subtasks?.length > 0 && (
                              <span className="ml-2 text-gray-400 font-normal normal-case">
                                ({selectedTicket.subtasks.filter(s => s.status === 'done').length}/{selectedTicket.subtasks.length} done)
                              </span>
                            )}
                          </Label>
                          <button onClick={() => setShowSubtaskForm(v => !v)} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100">
                            <Plus className="w-3 h-3" /> Add Subtask
                          </button>
                        </div>

                        {selectedTicket.subtasks?.length > 0 && (
                          <div className="space-y-1 mb-3">
                            {selectedTicket.subtasks.map(subtask => (
                              <div key={subtask.id} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 group">
                                {editingSubtaskId === subtask.id ? (
                                  <div className="space-y-2">
                                    <Input placeholder="Title" value={editingSubtaskForm.title} onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, title: e.target.value })} className="h-8 text-xs bg-white" />
                                    <Input placeholder="Description" value={editingSubtaskForm.description} onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, description: e.target.value })} className="h-8 text-xs bg-white" />
                                    <div className="flex gap-2">
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Assignee</Label>
                                        <Select value={editingSubtaskForm.assignee_email || 'unassigned'} onValueChange={val => setEditingSubtaskForm({ ...editingSubtaskForm, assignee_email: val === 'unassigned' ? '' : val })}>
                                          <SelectTrigger className="h-8 text-xs bg-white"><SelectValue placeholder="No assignee" /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="unassigned">No assignee</SelectItem>
                                            {user && !teamMembers.find(m => m.email === user.email) && (<SelectItem value={user.email}>Assign to myself</SelectItem>)}
                                            {teamMembers.map(m => (<SelectItem key={m.id} value={m.email}>{m.email === user?.email ? 'Assign to myself' : (m.full_name || m.email)}</SelectItem>))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Due Date</Label>
                                        <Input type="date" value={editingSubtaskForm.due_date || ''} onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, due_date: e.target.value })} className="h-8 text-xs bg-white" />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Time Estimate (h)</Label>
                                        <Input type="number" step="0.5" min="0" value={editingSubtaskForm.time_estimate || ''} onChange={e => setEditingSubtaskForm({ ...editingSubtaskForm, time_estimate: e.target.value })} className="h-8 text-xs bg-white" placeholder="e.g. 2.5" />
                                      </div>
                                      <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-semibold text-gray-700">Problem Type</Label>
                                        <ProblemTypeSearch value={editingSubtaskForm.problem_type} onChange={v => setEditingSubtaskForm({ ...editingSubtaskForm, problem_type: v })} placeholder="Select type..." allowCreate={true} inputClassName="h-8 text-xs" />
                                      </div>
                                    </div>
                                    <div className="flex-1 space-y-1">
                                      <Label className="text-xs font-semibold text-gray-700">Status</Label>
                                      <Select value={editingSubtaskForm.status || 'open'} onValueChange={val => setEditingSubtaskForm({ ...editingSubtaskForm, status: val })}>
                                        <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="open">Open</SelectItem>
                                          <SelectItem value="in_progress">In Progress</SelectItem>
                                          <SelectItem value="done">Done</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex gap-2 pt-1 border-t border-gray-100">
                                      <Button size="sm" className="h-7 px-3 text-xs bg-black text-white" onClick={() => handleUpdateSubtask(subtask.id)}>Save</Button>
                                      <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => setEditingSubtaskId(null)}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex items-start gap-2">
                                      <button onClick={() => handleToggleSubtaskStatus(subtask)} className="mt-0.5 flex-shrink-0">
                                        {subtask.status === 'done' ? <CheckSquare className="w-4 h-4 text-green-500" /> : subtask.status === 'in_progress' ? <Square className="w-4 h-4 text-yellow-500" /> : <Square className="w-4 h-4 text-gray-300" />}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-sm ${subtask.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{subtask.title}</span>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                          {subtask.assignee_name && <span className="text-xs text-gray-400">{subtask.assignee_name}</span>}
                                          {subtask.due_date && <span className="text-xs text-gray-400">{new Date(subtask.due_date).toLocaleDateString()}</span>}
                                          {subtask.time_estimate > 0 && <span className="text-xs text-blue-500">{subtask.time_estimate}h</span>}
                                          {subtask.status === 'in_progress' && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 rounded-full">In Progress</span>}
                                        </div>
                                      </div>
                                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                                        <ClickUpTaskButton taskId={subtask.clickup_subtask_id} iconOnly className="h-6 w-6 p-0" />
                                        <button onClick={() => { setEditingSubtaskId(subtask.id); setEditingSubtaskForm({ title: subtask.title, description: subtask.description || '', assignee_email: subtask.assignee_email || '', due_date: subtask.due_date || '', time_estimate: subtask.time_estimate || '', problem_type: subtask.tags?.[0] || '', status: subtask.status || 'open' }); }} className="p-1 hover:bg-gray-200 rounded">
                                          <Edit className="w-3 h-3 text-gray-500" />
                                        </button>
                                        <button onClick={() => handleDeleteSubtask(subtask.id)} className="p-1 hover:bg-red-100 rounded">
                                          <Trash2 className="w-3 h-3 text-red-400" />
                                        </button>
                                      </div>
                                    </div>
                                    {/* Subtask Comments */}
                                    <div className="mt-2 border-t border-gray-100 pt-1.5">
                                      <button className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" onClick={() => setExpandedSubtaskComments(prev => ({ ...prev, [subtask.id]: !prev[subtask.id] }))}>
                                        <MessageSquare className="w-3 h-3" />
                                        {subtask.comments?.length > 0 ? `${subtask.comments.length} comment${subtask.comments.length > 1 ? 's' : ''}` : 'Add comment'}
                                      </button>
                                      {expandedSubtaskComments[subtask.id] && (
                                        <div className="mt-2 space-y-2 pl-1">
                                          {(subtask.comments || []).map(c => (
                                            <div key={c.id} className="bg-white border border-gray-100 rounded-md px-2.5 py-1.5">
                                              <p className="text-xs text-gray-800 whitespace-pre-wrap">{c.text}</p>
                                              <p className="text-[10px] text-gray-400 mt-0.5">{c.created_by_email} · {new Date(c.created_at).toLocaleString()}</p>
                                            </div>
                                          ))}
                                          <div className="flex gap-2">
                                            <Input placeholder="Write a comment..." className="h-7 text-xs flex-1" value={subtaskCommentText[subtask.id] || ''} onChange={e => setSubtaskCommentText(prev => ({ ...prev, [subtask.id]: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') handleAddSubtaskComment(subtask.id); }} />
                                            <Button size="sm" className="h-7 px-2 text-xs bg-black text-white" onClick={() => handleAddSubtaskComment(subtask.id)} disabled={subtaskCommentLoading[subtask.id]}>
                                              {subtaskCommentLoading[subtask.id] ? '...' : 'Post'}
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {showSubtaskForm && (
                          <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
                            <h4 className="text-sm font-semibold text-gray-800">New Subtask</h4>
                            <Input placeholder="Subtask title *" value={subtaskInput} onChange={e => setSubtaskInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask(); if (e.key === 'Escape') setShowSubtaskForm(false); }} className="h-9 text-sm" autoFocus />
                            <Input placeholder="Description (optional)" value={subtaskDescription} onChange={e => setSubtaskDescription(e.target.value)} className="h-9 text-xs bg-white" />
                            <div className="flex gap-3">
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Assignee</Label>
                                <Select value={subtaskAssignee || 'unassigned'} onValueChange={val => setSubtaskAssignee(val === 'unassigned' ? '' : val)}>
                                  <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="No assignee" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">No assignee</SelectItem>
                                    {user && !teamMembers.find(m => m.email === user.email) && (<SelectItem value={user.email}>Assign to myself ({user.email})</SelectItem>)}
                                    {teamMembers.map(m => (<SelectItem key={m.id} value={m.email}>{m.email === user?.email ? 'Assign to myself' : (m.full_name || m.email)}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Due Date</Label>
                                <Input type="date" value={subtaskDueDate} onChange={e => setSubtaskDueDate(e.target.value)} className="h-9 text-xs bg-white" />
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Time Estimate (h)</Label>
                                <Input type="number" step="0.5" min="0" value={subtaskTimeEstimate} onChange={e => setSubtaskTimeEstimate(e.target.value)} className="h-9 text-xs bg-white" placeholder="e.g. 2.5" />
                              </div>
                              <div className="flex-1 space-y-1">
                                <Label className="text-xs font-semibold text-gray-700">Problem Type</Label>
                                <ProblemTypeSearch value={subtaskProblemType} onChange={v => setSubtaskProblemType(v)} placeholder="Select type..." allowCreate={true} />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1 border-t border-gray-200/60">
                              <Button className="h-9 px-4 text-xs bg-black text-white" onClick={handleAddSubtask} disabled={subtaskLoading || !subtaskInput.trim()}>
                                {subtaskLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                                {subtaskLoading ? 'Creating...' : 'Create Subtask'}
                              </Button>
                              <Button variant="outline" className="h-9 px-4 text-xs" onClick={() => setShowSubtaskForm(false)}>Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time Logs Section */}
                      {getTicketHoursIncludingSubtasks(selectedTicket) > 0 && (
                        <div>
                          <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block flex items-center gap-2">
                            Time Logs
                            <span className="text-xs font-normal text-gray-400 normal-case">(Logged + subtask estimates: {getTicketHoursIncludingSubtasks(selectedTicket)}h)</span>
                          </Label>
                          <div className="bg-white border boundary-gray-200 rounded-lg divide-y divide-gray-100">
                            {selectedTicket.time_logs && selectedTicket.time_logs.length > 0 && (
                              <div className="max-h-40 overflow-y-auto">
                                {selectedTicket.time_logs.map((log) => (
                                  <div key={log.id} className="p-3 text-sm hover:bg-gray-50 transition-colors group/log">
                                    {editingTimeLogId === log.id ? (
                                      <div className="space-y-2">
                                        <div className="flex gap-2">
                                          <Input
                                            type="number"
                                            step="0.1"
                                            value={editingTimeLogForm.hours}
                                            onChange={(e) => setEditingTimeLogForm({ ...editingTimeLogForm, hours: e.target.value })}
                                            className="w-24 h-8 text-sm"
                                            placeholder="Hours"
                                          />
                                          <Input
                                            type="date"
                                            value={editingTimeLogForm.logged_at || (log.logged_at ? log.logged_at.substring(0, 10) : '')}
                                            onChange={(e) => setEditingTimeLogForm({ ...editingTimeLogForm, logged_at: e.target.value })}
                                            className="w-36 h-8 text-sm"
                                          />
                                          <Input
                                            value={editingTimeLogForm.description}
                                            onChange={(e) => setEditingTimeLogForm({ ...editingTimeLogForm, description: e.target.value })}
                                            className="flex-1 h-8 text-sm"
                                          />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                          <Button variant="ghost" size="sm" onClick={() => setEditingTimeLogId(null)} className="h-7 px-2 text-xs">Cancel</Button>
                                          <Button size="sm" onClick={() => handleUpdateTimeLog(log.id)} className="h-7 px-2 text-xs bg-black hover:bg-gray-800">Save</Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex justify-between items-center mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-900">{log.hours}h</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 w-5 p-0 opacity-0 group-hover/log:opacity-100 transition-opacity"
                                              onClick={() => {
                                                setEditingTimeLogId(log.id);
                                              setEditingTimeLogForm({ hours: log.hours, description: log.description, logged_at: log.logged_at ? log.logged_at.substring(0, 10) : '' });
                                              }}
                                            >
                                              <Edit className="w-3 h-3 text-gray-500" />
                                            </Button>
                                          </div>
                                          <span className="text-xs text-gray-500">{new Date(log.logged_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                          <p className="text-gray-600 truncate flex-1 mr-4">{log.description}</p>
                                          <span className="text-xs text-gray-400 truncate max-w-[120px]">{log.logged_by_email}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <Button
                        data-testid="log-time-button"
                        onClick={() => setTimeLogDialogOpen(true)}
                        className="w-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 shadow-sm"
                      >
                        <Clock className="w-4 h-4 mr-2 text-gray-500" />
                        Log Additional Time
                      </Button>

                      {/* Paid Support Toggle */}
                      <div className={`rounded-lg p-3 border ${selectedTicket.is_paid_support ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="is_paid_support"
                            checked={!!selectedTicket.is_paid_support}
                            onChange={(e) => handleUpdateTicketField(selectedTicket.id, 'is_paid_support', e.target.checked)}
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          />
                          <label htmlFor="is_paid_support" className={`text-sm font-medium cursor-pointer ${selectedTicket.is_paid_support ? 'text-emerald-800' : 'text-gray-700'}`}>
                            Paid Support Task
                          </label>
                          {selectedTicket.is_paid_support && (
                            <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Active</span>
                          )}
                        </div>
                        {selectedTicket.is_paid_support && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-sm text-emerald-700 font-medium">Amount ($)</span>
                            <div className="relative flex-1">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="pl-6 h-8 text-sm w-full"
                                defaultValue={selectedTicket.paid_amount || ''}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  if (val !== (selectedTicket.paid_amount || 0)) {
                                    handleUpdateTicketField(selectedTicket.id, 'paid_amount', val);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <Separator className="bg-gray-100" />

                  {/* Comments Section */}
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 block">Discussion & Updates</Label>

                    {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                      <div className="space-y-4" data-testid="comments-list">
                        {selectedTicket.comments.map((comment) => (
                          <div key={comment.id} className="flex gap-3 group">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-500 uppercase border border-gray-200">
                              {comment.created_by_email.substring(0, 2)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {comment.source === 'tracking'
                                      ? (selectedTicket.requested_by_name || 'Requester')
                                      : comment.created_by_email}
                                  </span>
                                  {comment.source === 'tracking' && (
                                    <Badge className="text-[10px] h-5 px-1.5 font-normal bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50">
                                      Requester via tracking page
                                    </Badge>
                                  )}
                                  {comment.source === 'clickup' && (
                                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal text-gray-500 bg-gray-50">ClickUp</Badge>
                                  )}
                                  {(comment.created_by === user?.id || isAdminOrSubadmin) && comment.source !== 'clickup' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditingCommentId(comment.id);
                                        setEditingCommentText(comment.text);
                                      }}
                                      className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity -ml-1"
                                    >
                                      <Edit className="w-3 h-3 text-gray-400 hover:text-gray-900" />
                                    </Button>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 tabular-nums">
                                  {new Date(comment.created_at).toLocaleString(undefined, {
                                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                                  })}
                                </span>
                              </div>

                              {editingCommentId === comment.id ? (
                                <div className="space-y-2 mt-1">
                                  <Textarea
                                    value={editingCommentText}
                                    onChange={(e) => setEditingCommentText(e.target.value)}
                                    rows={3}
                                    className="bg-white text-sm"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)} className="h-7 text-xs">Cancel</Button>
                                    <Button size="sm" onClick={() => handleUpdateComment(comment.id)} className="h-7 text-xs bg-black hover:bg-gray-800">Save</Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-gray-50 rounded-lg rounded-tl-none p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-100 group-hover:border-gray-200 transition-colors">
                                  {comment.text}
                                </div>
                              )}

                              {comment.attachments && comment.attachments.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2 ml-1">
                                  {comment.attachments.map((url, idx) => {
                                    const imageUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                                    return (
                                      <a key={idx} href={imageUrl} target="_blank" rel="noopener noreferrer" className="block group/img relative overflow-hidden rounded-md border border-gray-200">
                                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/5 transition-colors" />
                                        <img
                                          src={imageUrl}
                                          alt="Attachment"
                                          className="h-16 w-16 object-cover"
                                        />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No comments yet.</p>
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleAddComment} className="space-y-3">
                    <Textarea
                      data-testid="comment-input"
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                    />

                    {/* Attachments Preview */}
                    {commentAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {commentAttachments.map((url, idx) => {
                          const imageUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                          return (
                            <div key={idx} className="relative group">
                              <img src={imageUrl} alt="Preview" className="h-16 w-16 object-cover rounded border" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(idx)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <input
                          type="file"
                          id="file-upload"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept="image/*"
                        />
                        <label
                          htmlFor="file-upload"
                          className="cursor-pointer text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
                          title="Attach Image"
                        >
                          <Paperclip className="w-5 h-5" />
                        </label>
                      </div>

                      <Button
                        type="submit"
                        data-testid="add-comment-button"
                        disabled={commentLoading || (!commentText.trim() && commentAttachments.length === 0)}
                        className="bg-black hover:bg-gray-800"
                      >
                        {commentLoading ? 'Adding...' : 'Add Comment'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </Dialog>

      {/* Time Log Dialog */}
      <Dialog open={timeLogDialogOpen} onOpenChange={setTimeLogDialogOpen}>
        <DialogContent data-testid="time-log-dialog" className="sm:max-w-md" overlayClassName="z-[9998]" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>Log Time</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTimeLog} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hours">Hours Spent</Label>
                <Input
                  id="hours"
                  type="number"
                  step="0.1"
                  min="0.1"
                  data-testid="time-hours-input"
                  placeholder="e.g., 2.5"
                  value={timeLog.hours}
                  onChange={(e) => setTimeLog({ ...timeLog, hours: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="log-date">Date Worked</Label>
                <Input
                  id="log-date"
                  type="date"
                  value={timeLog.logged_at}
                  onChange={(e) => setTimeLog({ ...timeLog, logged_at: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="time-description">Description</Label>
              <Textarea
                id="time-description"
                data-testid="time-description-input"
                placeholder="Describe the work done..."
                value={timeLog.description}
                onChange={(e) => setTimeLog({ ...timeLog, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="submit-time-log-button"
              disabled={timeLogLoading}
              className="w-full bg-black hover:bg-gray-800"
            >
              {timeLogLoading ? 'Logging...' : 'Log Time'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inline Add Company Dialog */}
      <Dialog open={addCompanyOpen} onOpenChange={(v) => { setAddCompanyOpen(v); if (!v) setNewCompanyForm({ name: '', url: '', notes: '' }); }}>
        <DialogContent className="sm:max-w-md" overlayClassName="z-[9998]" style={{ zIndex: 10000 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Create New Company
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompanyInline} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                value={newCompanyForm.name}
                onChange={e => setNewCompanyForm({ ...newCompanyForm, name: e.target.value })}
                placeholder="Acme Corp"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                value={newCompanyForm.url}
                onChange={e => setNewCompanyForm({ ...newCompanyForm, url: e.target.value })}
                placeholder="https://acme.com"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setAddCompanyOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-black hover:bg-gray-800">Create &amp; Select</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
